/**
 * NAD Product Gallery Component
 * Swiper.js powered product image gallery with thumbnails and zoom
 */

// Swiper imports (ensure Swiper.js is installed)
import Swiper from 'swiper';
import { Navigation, Keyboard, A11y, FreeMode, Pagination, EffectFade, Controller, Thumbs } from 'swiper/modules';

// PhotoSwipe imports (ensure PhotoSwipe is installed)
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';
import { SwiperOptions } from 'swiper/types';

// Types
interface GalleryConfig {
  sectionId: string;
  enableZoom: boolean;
  mediaCount: number;
  imagesCount: number;
  videosCount: number;
  autoHeight: boolean;
  loop: boolean;
  spaceBetween: number;
  thumbsSpaceBetween: number;
  breakpoints: {
    [key: string]: {
      spaceBetween: number;
      thumbsSpaceBetween: number;
    };
  };
}

interface GalleryElements {
  container: HTMLElement;
  mainSwiper: HTMLElement;
  thumbsSwiper: HTMLElement | null;
  photoswipeTriggers: NodeListOf<HTMLElement>;
  photoswipeData: HTMLScriptElement | null;
  playTriggers: NodeListOf<HTMLElement>;
  videos: NodeListOf<HTMLVideoElement>;
  configScript: HTMLScriptElement | null;
}

export class NadProductGallery {
  private element: HTMLElement;
  private elements: GalleryElements;
  private config: GalleryConfig;
  private mainSwiperInstance: Swiper | null = null;
  private thumbsSwiperInstance: Swiper | null = null;
  private photoswipe: PhotoSwipeLightbox | null = null;
  private isUpdating: boolean = false; // Flag to prevent infinite loops
  private variantMediaMap: Record<string, number> = {}; // variant id -> featured media id
  private sectionRoot: HTMLElement | Document;
  private handleVariantChangeBound: (event: Event) => void;

  constructor(element: HTMLElement) {
    this.element = element;
    this.elements = this.getElements();
    this.config = this.getConfig();
    this.sectionRoot = (element.closest('.shopify-section') as HTMLElement | null) || document;
    this.variantMediaMap = this.getVariantMediaMap();
    this.handleVariantChangeBound = this.handleVariantChange.bind(this);
    this.init();
  }

  private getElements(): GalleryElements {
    return {
      container: this.element,
      mainSwiper: this.element.querySelector('[data-swiper="main"]') as HTMLElement,
      thumbsSwiper: this.element.querySelector('[data-swiper="thumbs"]'),
      photoswipeTriggers: this.element.querySelectorAll('[data-pswp-src]'),
      photoswipeData: this.element.querySelector('[data-photoswipe-gallery]'),
      playTriggers: this.element.querySelectorAll('[data-play-trigger]'),
      videos: this.element.querySelectorAll('video'),
      configScript: this.element.querySelector('[data-gallery-config]'),
    };
  }

  private getConfig(): GalleryConfig {
    const defaultConfig: GalleryConfig = {
      sectionId: 'nad-product-gallery',
      enableZoom: true,
      mediaCount: 0,
      imagesCount: 0,
      videosCount: 0,
      autoHeight: false,
      loop: false,
      spaceBetween: 16,
      thumbsSpaceBetween: 10,
      breakpoints: {
        '768': {
          spaceBetween: 20,
          thumbsSpaceBetween: 12,
        },
      },
    };

    if (this.elements.configScript) {
      try {
        const configData = JSON.parse(this.elements.configScript.textContent || '{}');
        return { ...defaultConfig, ...configData };
      } catch (error) {
        console.warn('Failed to parse gallery config:', error);
      }
    }

    return defaultConfig;
  }

  /**
   * Parse the variant -> featured media id map emitted by the snippet.
   * Lets the gallery slide to the matching flavor image on variant change.
   */
  private getVariantMediaMap(): Record<string, number> {
    // The snippet renders this script inside the gallery, but fall back to the
    // section root in case markup/whitespace changes move it just outside.
    const script =
      this.element.querySelector('[data-gallery-variant-media]') ||
      this.sectionRoot.querySelector('[data-gallery-variant-media]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}') as Record<string, number>;
    } catch (error) {
      console.warn('Failed to parse gallery variant media map:', error);
      return {};
    }
  }

  private init(): void {
    // Add small delay to ensure DOM is fully loaded
    setTimeout(() => {
      this.validateElements();
      this.initSwipers();
      this.initPhotoSwipe();
      this.initVideos();
      this.bindEvents();
    }, 50);
  }

  private validateElements(): void {
    console.log('Validating gallery elements...', {
      container: !!this.element,
      mainSwiper: !!this.elements.mainSwiper,
      thumbsSwiper: !!this.elements.thumbsSwiper,
      thumbsWrapper: !!this.elements.thumbsSwiper?.querySelector('.swiper-wrapper'),
      thumbsSlides: this.elements.thumbsSwiper?.querySelectorAll('.swiper-slide').length || 0,
      config: this.config
    });
  }

  private initSwipers(): void {
    // Debug logging
    console.log('Initializing swipers...', {
      thumbsSwiper: !!this.elements.thumbsSwiper,
      thumbsElement: this.elements.thumbsSwiper,
      mediaCount: this.config.mediaCount,
      mainSwiper: !!this.elements.mainSwiper,
      thumbsSlides: this.elements.thumbsSwiper?.querySelectorAll('.swiper-slide').length || 0
    });

    // --- Initialize main swiper first ---
    if (this.elements.mainSwiper) {
      const swiperConfig: any = {
        modules: [Navigation, A11y, FreeMode, Thumbs],
        spaceBetween: this.config.spaceBetween,
        slidesPerView: 1,
        centeredSlides: true,
        navigation: {
          nextEl: this.element.querySelector('.nad-product-gallery__nav-btn--next') as HTMLElement,
          prevEl: this.element.querySelector('.nad-product-gallery__nav-btn--prev') as HTMLElement,
        },
        thumbs: {
          swiper: null, // Will be set after thumbs swiper is initialized
        },
        on:{
          init(swiper: Swiper): void {
            swiper.navigation?.destroy();
            const breakpoint: string = swiper.getBreakpoint(swiper.params.breakpoints);
            const currentBreakpoint: SwiperOptions | undefined = swiper.params.breakpoints?.[breakpoint];
            const { navigation: currentBreakpointNavConfig } = currentBreakpoint || {};

            if (!currentBreakpointNavConfig) {
                return;
            }

            if (currentBreakpointNavConfig === true || currentBreakpointNavConfig?.enabled) {
                swiper.navigation.init();
                swiper.navigation.update();
            }
          },
        },
        a11y: {
          enabled: true,
        },
        breakpoints: {
          768: {
            spaceBetween: this.config.breakpoints['768']?.spaceBetween || 20,
            slidesPerView: 1,
          },
        },
      };

      try {
        this.mainSwiperInstance = new Swiper(this.elements.mainSwiper, swiperConfig);

        console.log('Main swiper initialized successfully');
      } catch (error) {
        console.error('Failed to initialize main swiper:', error);
      }
    }

    // --- Then initialize thumbnails swiper (if exists) ---
    if (this.elements.thumbsSwiper) {
      try {
        this.thumbsSwiperInstance = new Swiper(this.elements.thumbsSwiper, {
          modules: [FreeMode, Thumbs, Navigation],
          spaceBetween: this.config.thumbsSpaceBetween,
          slidesPerView: 4,
          freeMode: true,
          watchSlidesProgress: true,
          navigation: {
            nextEl: this.elements.thumbsSwiper?.querySelector('.nad-product-gallery__thumbs-nav-btn--next') as HTMLElement,
            prevEl: this.elements.thumbsSwiper?.querySelector('.nad-product-gallery__thumbs-nav-btn--prev') as HTMLElement,
          },
          breakpoints: {
            768: {
              spaceBetween: this.config.breakpoints['768']?.thumbsSpaceBetween || 12,
              slidesPerView: 5,
            },
          },
        });

        // Connect thumbs swiper to main swiper
        if (this.mainSwiperInstance) {
          this.mainSwiperInstance.thumbs.swiper = this.thumbsSwiperInstance;
          this.mainSwiperInstance.thumbs.init();
          this.mainSwiperInstance.thumbs.update(true);
          
          // Fallback: Add manual click events if thumbs connection doesn't work
          setTimeout(() => {
            this.addThumbnailClickEvents();
          }, 100);
        }

        console.log('Thumbnails swiper initialized successfully', this.thumbsSwiperInstance);
      } catch (error) {
        console.error('Failed to initialize thumbnails swiper:', error);
      }
    } else {
      console.log('Thumbnails swiper not initialized:', {
        thumbsElement: !!this.elements.thumbsSwiper,
        mediaCount: this.config.mediaCount
      });
    }
 
  }
 
  private initPhotoSwipe(): void {
    if (!this.config.enableZoom) return;

    try {
      // Initialize PhotoSwipe Lightbox with simple configuration
      this.photoswipe = new PhotoSwipeLightbox({
        gallery: this.element,
        children: 'a[data-pswp-src], button[data-pswp-src]',
        pswpModule: PhotoSwipe,
        bgOpacity: 0.9,
        showHideAnimationType: 'zoom',
        
        // Custom options
        closeOnVerticalDrag: true,
        pinchToClose: true,
        padding: { top: 40, bottom: 40, left: 40, right: 40 },
      });

      // Handle events
      this.photoswipe.on('beforeOpen', () => {
        // Pause all videos when opening PhotoSwipe
        this.pauseAllVideos();
      });

      // Initialize PhotoSwipe
      this.photoswipe.init();
      
      console.log('PhotoSwipe initialized successfully');
    } catch (error) {
      console.error('Failed to initialize PhotoSwipe:', error);
    }
  }

  private initVideos(): void {
    if (this.config.videosCount === 0) return;

    // Set up video lazy loading and controls
    this.elements.videos.forEach((video) => {
      // Pause video when slide changes
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = 0;
      });

      // Handle video end
      video.addEventListener('ended', () => {
        this.showPlayButton(video);
      });

      // Handle video play
      video.addEventListener('play', () => {
        this.hidePlayButton(video);
        this.pauseOtherVideos(video);
      });

      // Handle video pause
      video.addEventListener('pause', () => {
        this.showPlayButton(video);
      });
    });
  }

  private bindEvents(): void {
    // Video play trigger events
    this.elements.playTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleVideoPlay.bind(this));
    });

    // PhotoSwipe handles zoom events automatically
    // Note: slideChange events are handled in initSwipers() for better sync

    // Sync the gallery image to the selected flavor/variant.
    // The Combined Listings swatch updates input[name="id"] and fires a native
    // "change" event, so we listen within this gallery's section for that input.
    if (Object.keys(this.variantMediaMap).length > 0) {
      this.sectionRoot.addEventListener('change', this.handleVariantChangeBound);
      // Align the gallery with the variant that is already selected on load.
      const currentVariantId = this.getCurrentVariantId();
      if (currentVariantId) this.slideToVariant(currentVariantId);
    }
  }

  /** Read the currently selected variant id from this section's product form. */
  private getCurrentVariantId(): string | null {
    const input = this.sectionRoot.querySelector<HTMLInputElement>(
      'input[name="id"].product-variant-id, input[name="id"]'
    );
    return input?.value || null;
  }

  private handleVariantChange(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target || typeof target.matches !== 'function') return;
    if (!target.matches('input[name="id"]')) return;
    this.slideToVariant((target as HTMLInputElement).value);
  }

  /** Slide the gallery to the featured media of the given variant, if mapped. */
  private slideToVariant(variantId: string): void {
    const mediaId = this.variantMediaMap[variantId];
    if (mediaId == null) return;
    this.slideToMediaId(mediaId);
  }

  /** Slide the main gallery (and synced thumbnails) to the slide for a media id. */
  private slideToMediaId(mediaId: number): void {
    if (!this.mainSwiperInstance) return;
    const slides = Array.from(
      this.elements.mainSwiper.querySelectorAll<HTMLElement>('.nad-product-gallery__slide')
    );
    const index = slides.findIndex(
      (slide) => slide.dataset.mediaId === String(mediaId)
    );
    if (index < 0 || index === this.mainSwiperInstance.activeIndex) return;
    this.mainSwiperInstance.slideTo(index);
  }


  private handleVideoPlay(event: Event): void {
    event.preventDefault();
    
    const trigger = event.currentTarget as HTMLElement;
    const videoContainer = trigger.closest('.nad-product-gallery__video-container');
    const video = videoContainer?.querySelector('video');
    
    if (video) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  }

  private pauseAllVideos(): void {
    this.elements.videos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  }

  private pauseOtherVideos(currentVideo: HTMLVideoElement): void {
    this.elements.videos.forEach((video) => {
      if (video !== currentVideo && !video.paused) {
        video.pause();
      }
    });
  }

  private showPlayButton(video: HTMLVideoElement): void {
    const container = video.closest('.nad-product-gallery__video-container');
    const playBtn = container?.querySelector('.nad-product-gallery__play-btn') as HTMLElement;
    if (playBtn) {
      playBtn.style.opacity = '1';
      playBtn.style.pointerEvents = 'auto';
    }
  }

  private hidePlayButton(video: HTMLVideoElement): void {
    const container = video.closest('.nad-product-gallery__video-container');
    const playBtn = container?.querySelector('.nad-product-gallery__play-btn') as HTMLElement;
    if (playBtn) {
      playBtn.style.opacity = '0';
      playBtn.style.pointerEvents = 'none';
    }
  }

  /**
   * Add manual click events to thumbnails as fallback
   */
  private addThumbnailClickEvents(): void {
    if (!this.thumbsSwiperInstance || !this.mainSwiperInstance) return;

    this.thumbsSwiperInstance.slides.forEach((slide: HTMLElement, index: number) => {
      slide.addEventListener('click', () => {
        console.log('Thumbnail clicked:', index);
        this.mainSwiperInstance?.slideTo(index);
      });
    });
  }

  public destroy(): void {
    // Pause and clean up videos
    this.pauseAllVideos();

    // Destroy PhotoSwipe
    if (this.photoswipe) {
      this.photoswipe.destroy();
      this.photoswipe = null;
    }

    // Destroy swiper instances
    if (this.mainSwiperInstance) {
      this.mainSwiperInstance.destroy(true, true);
      this.mainSwiperInstance = null;
    }

    if (this.thumbsSwiperInstance) {
      this.thumbsSwiperInstance.destroy(true, true);
      this.thumbsSwiperInstance = null;
    }

    // Remove event listeners
    this.elements.playTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleVideoPlay.bind(this));
    });
    this.sectionRoot.removeEventListener('change', this.handleVariantChangeBound);

    // Clean up body styles
    document.body.style.overflow = '';
  }

  // Static method for automatic initialization
  static initializeAll(): void {
    const elements = document.querySelectorAll<HTMLElement>('.nad-product-gallery');
    elements.forEach((element) => {
      if (!(element as any)._nadProductGalleryInstance) {
        (element as any)._nadProductGalleryInstance = new NadProductGallery(element);
      }
    });
  }

  // Static method for cleanup
  static destroyAll(): void {
    const elements = document.querySelectorAll<HTMLElement>('.nad-product-gallery');
    elements.forEach((element) => {
      const instance = (element as any)._nadProductGalleryInstance;
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
        delete (element as any)._nadProductGalleryInstance;
      }
    });
  }
}
